
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { TeamUserMixin } from '@/mixins';
import { ROUTER } from '@/router/constants';
import { MemberRow } from '@/utils/membersApi';
import {
	FailedTeamIdsMap,
	MemberTeamChips,
	MembershipsMap,
	everyNamedTeamGone,
	isCompanyRoleInviteOnly,
	memberDashboardTeamId,
	memberTeamChips,
} from './membersDirectoryState';

/**
 * The person's teams as chips. Names come from the Postgres row; each chip
 * exists only while its bound membership doc does, so removing the person from
 * a team drops the chip live. Only the teams the row watches (`boundTeamIds`)
 * get a chip, the rest fold into "+N more"; a team whose bind failed is drawn
 * from the Postgres ref rather than left on a skeleton. A chip links to the
 * team dashboard only when the viewer may open that team. The carrier team of
 * a company-role invite gets no chip, because nobody chose this person for it:
 * the row still binds that doc, and the cell says the person is on none.
 * It says the same once every team the row names is known to be gone, which the
 * row only shows while the person still holds a company role.
 */
export default Vue.extend({
	name: 'MemberTeamsTableCell',
	mixins: [
		TeamUserMixin,
	],
	props: {
		row: {
			type: Object as () => MemberRow,
			required: true,
		},
		memberships: {
			type: Object as () => MembershipsMap,
			default: () => ({}),
		},
		failedTeamIds: {
			type: Object as () => FailedTeamIdsMap,
			default: () => ({}),
		},
		// The teams the row watches, in row order. Nothing else can have a chip.
		boundTeamIds: {
			type: Array as () => string[],
			default: () => [],
		},
	},
	computed: {
		...mapState([
			'user',
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		teamChips (): MemberTeamChips {
			return memberTeamChips(this.row, this.memberships, this.failedTeamIds, this.boundTeamIds);
		},
		// Every team the row names is a stub carrier, or is gone: shown as on none.
		showsNoTeams (): boolean {
			return isCompanyRoleInviteOnly(this.row, this.memberships) ||
				everyNamedTeamGone(this.row, this.memberships, this.boundTeamIds);
		},
		cannotOpenTooltip (): string {
			return `Switch to this ${this.displayLabels.team} to open its dashboard`;
		},
		// The team whose member dashboard "+N more" opens, or null for none.
		dashboardTeamId (): string | null {
			return memberDashboardTeamId(this.row, this.memberships, {
				currentTeamId: this.team?.id || null,
				canOpenAnyTeam: !!(this.hasManageTeamsPermission || this.user?.isAdmin),
			});
		},
	},
	methods: {
		canOpenTeam (teamId: string): boolean {
			return !!(
				this.hasManageTeamsPermission ||
				this.user?.isAdmin ||
				(this.team?.id && this.team.id === teamId)
			);
		},
		onOpenTeam (teamId: string) {
			if (!this.canOpenTeam(teamId)) return;
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.TEAM_DASHBOARD,
				params: {
					teamId,
				},
				query: this.$route.query,
			});
		},
		onViewMember () {
			const teamId: string | null = this.dashboardTeamId;
			if (!teamId || !this.row?.email) return;
			this.routerPush(this.$route, this.$router, {
				name: ROUTER.TEAM_MEMBER_DASHBOARD,
				params: {
					teamId,
					// The membership doc's id. Raw: vue-router encodes params on push and decodes them on match.
					teamMemberId: this.row.email,
				},
				// Carried in as it is carried back out by the member dashboard's crumbs.
				query: this.$route.query,
			});
		},
	},
});
